/**
 * WorkoutCoachSession - Conversational AI Workout Coach
 *
 * Maintains a growing conversation context throughout the workout session.
 * The AI builds understanding over time, making responses smarter and more contextual.
 *
 * Ported from kinasticCoach/App/Services/AI/WorkoutCoachSession.ts
 */
import type { ViewWorkout } from '../../Model/WorkoutLog/ViewWorkout';
import type { ViewSet } from '../../Model/WorkoutLog/ViewSet';

const LOG_PREFIX = '[CoachSession]';

export type CoachMessage = { role: 'system' | 'user' | 'assistant'; content: string };

export type CoachEvent =
  | 'workout_start'
  | 'set_starting'
  | 'set_complete'
  | 'break_reminder'
  | 'exercise_intro'
  | 'value_changed'
  | 'milestone'
  | 'workout_complete';

export type CoachEventContext = {
  event: CoachEvent;
  viewSet: ViewSet;
  language: string;
  previousReps?: number;
  previousWeight?: number;
  previousDuration?: number;
  remainingBreakSeconds?: number;
  workoutProgressPercent?: number;
};

function buildCompactWorkoutPlan(viewWorkout: ViewWorkout, language: string): string {
  const phases = viewWorkout.viewPhases ?? [];
  const lines: string[] = [];

  const workoutName = viewWorkout.workout?.getName(language) || 'Custom Workout';
  lines.push(`WORKOUT: ${workoutName}`);
  lines.push(`Total exercises: ${viewWorkout.sets?.length ?? 0}`);
  lines.push('');

  for (const phase of phases) {
    if (!phase) continue;
    const phaseName = phase.phase?.type || 'Main';
    lines.push(`## ${phaseName.toUpperCase()}`);

    const blocks = phase.exerciseBlocks ?? [];
    for (const block of blocks) {
      if (!block) continue;
      const exercise = block.exercise;
      const exerciseName = exercise ? exercise.localeName(language) : 'Unknown';
      const sets = block.sets ?? [];
      const setsCount = sets.length;
      const firstSet = sets[0];

      const reps = firstSet?.blockSet?.values?.['REPETITIONS'];
      const weight = firstSet?.blockSet?.values?.['WEIGHT'];
      const duration = firstSet?.blockSet?.values?.['DURATION'];
      const breakTime = firstSet?.plannedBreakTime;

      let details = `${setsCount} set${setsCount > 1 ? 's' : ''}`;
      if (reps) details += ` x ${reps} reps`;
      if (weight) details += ` @ ${weight}kg`;
      if (duration) details += `, ${duration}s`;
      if (breakTime) details += `, ${breakTime}s break`;

      const muscles = exercise?.bodyParts
        ?.map((bp) => bp.names.get(language) || bp.name)
        .filter(Boolean)
        .join(', ');

      lines.push(`- ${exerciseName} (${details})${muscles ? ` [${muscles}]` : ''}`);
    }
    lines.push('');
  }

  return lines.join('\n');
}

function buildSystemPrompt(language: string): string {
  const langName =
    language === 'de'
      ? 'German'
      : language === 'fr'
        ? 'French'
        : language === 'it'
          ? 'Italian'
          : language === 'es'
            ? 'Spanish'
            : 'English';

  const informalNote =
    language === 'de'
      ? 'ALWAYS use "Du" (informal), NEVER "Sie"'
      : language === 'fr'
        ? 'ALWAYS use "Tu" (informal), NEVER "Vous"'
        : language === 'it'
          ? 'ALWAYS use "Tu" (informal), NEVER "Lei"'
          : language === 'es'
            ? 'ALWAYS use "Tu" (informal), NEVER "Usted"'
            : 'Use friendly, casual English';

  const terminology =
    language === 'de'
      ? `## TERMINOLOGY (DEUTSCH)
- SET = SATZ. "Satz 2 von 3" / "Wiederholung 2 von 3"
- REPS = WIEDERHOLUNGEN (einzelne Bewegungen innerhalb eines Satzes)
- Never confuse Satz (set) with Wiederholung (rep). "3 Satze a 12 Wiederholungen"`
      : language === 'fr'
        ? `## TERMINOLOGY (FRANCAIS)
- SET = SERIE. "Serie 2 sur 3"
- REPS = REPETITIONS (mouvements individuels dans une serie)
- Ne confonds jamais serie et repetition.`
        : language === 'it'
          ? `## TERMINOLOGY (ITALIANO)
- SET = SERIE. "Serie 2 di 3"
- REPS = RIPETIZIONI (movimenti individuali in una serie)
- Non confondere mai serie e ripetizioni.`
          : `## TERMINOLOGY
- SET = a round/group of reps. "Set 2 of 3"
- REPS = individual movements within a set. "12 reps"
- Never confuse sets with reps.`;

  return `You are a knowledgeable workout coach guiding someone in real-time. Supportive gym buddy tone - like a smart training partner who knows their stuff.

## RULES
- ${informalNote}
- ONLY speak ${langName.toUpperCase()} — every single word must be ${langName}. Never mix in words from other languages.
- ONLY reference what the EVENT DATA tells you. Never invent exercises, reps, or details not in the context.
- Vary your phrasing — never repeat the same line in a workout.

${terminology}

## WHAT TO SAY
SET_STARTING: Name the exercise + reps/weight. Add a quick form cue or what muscles they'll hit.
SET_COMPLETE: Acknowledge performance naturally. Connect it to what's next or what they just achieved. If they changed weight/reps, comment on it.
EXERCISE_INTRO: Name the next exercise, explain what it targets and why it matters. Give one specific form tip. This is break time — they can absorb info!
BREAK_REMINDER: Prepare them mentally — name what's coming, maybe a quick cue to focus on.
VALUE_CHANGED: Acknowledge the change and briefly say why it's smart (or encourage them).
WORKOUT_START: Welcome them, name the workout, mention what muscle groups they'll hit today. Build anticipation.
WORKOUT_COMPLETE: Celebrate! Summarize what they accomplished — mention key exercises or muscle groups they worked.
MILESTONE: Acknowledge progress, mention what's been done and what's ahead.

## LENGTH LIMITS
- SET_STARTING / SET_COMPLETE / VALUE_CHANGED: 1-2 sentences, max 20 words
- BREAK / EXERCISE_INTRO: 2-3 sentences, max 40 words (use break time to educate!)
- BREAK_REMINDER: 1-2 sentences, max 20 words
- WORKOUT_START / WORKOUT_COMPLETE / MILESTONE: 2-3 sentences, max 35 words
- Adapt to available speech time: short breaks = shorter messages, long breaks = share useful knowledge

## OUTPUT: PLAIN TEXT
Respond with plain spoken text only. No tags, no markup, no formatting. Just natural sentences as you would speak them.

## BE SMART
- During BREAKS: share useful info — what muscles are being worked, form tips, how this exercise connects to the previous one
- During ACTIVE sets: keep it punchy and motivating
- Reference their actual data (reps, weight, sets) naturally
- Sound like a coach who genuinely understands the workout
- OCCASIONALLY (not every time!) drop a specific, interesting fact about the exercise or muscle group — but only when it's genuinely relevant and adds value. Example: "Deadlifts activate over 70% of your muscles — that's why they're so effective." NOT generic trivia.

## DON'T
- Mix languages — if speaking English, never say "Satz" or "von". If speaking German, never say "set" or "of".
- Use generic filler ("Great job, you're really crushing it today, keep up the amazing work!")
- Narrate what's obvious ("You just completed set 2")
- Sound like a GPS or robot
- Say "last set" for single-set exercises
- Drop fun facts every single time — save them for moments where they fit naturally (maybe 1-2 per workout)`;
}

function buildEventContext(ctx: CoachEventContext): string {
  const { event, viewSet, language, previousReps, previousWeight, previousDuration, remainingBreakSeconds } = ctx;

  const exercise = viewSet.exercise;
  const block = viewSet.viewBlock;
  const workout = block.viewPhase.viewWorkout;

  const allSets = workout.sets;
  const currentIndex = allSets.findIndex((s) => s.id === viewSet.id);
  const totalSets = allSets.length;
  const progressPercent = Math.round(((currentIndex + 1) / totalSets) * 100);

  const reps = viewSet.logSet?.values.get('REPETITIONS') ?? viewSet.blockSet?.values?.['REPETITIONS'];
  const weight = viewSet.logSet?.values.get('WEIGHT') ?? viewSet.blockSet?.values?.['WEIGHT'];
  const duration = viewSet.logSet?.values.get('DURATION') ?? viewSet.blockSet?.values?.['DURATION'];

  const nextSet = viewSet.nextSet;
  const nextExercise = nextSet?.exercise;
  const isNewExerciseNext = nextExercise && nextExercise.id !== exercise?.id;

  const lines: string[] = [];

  lines.push(`EVENT: ${event.toUpperCase()}`);
  lines.push(`PROGRESS: ${progressPercent}% (set ${currentIndex + 1}/${totalSets})`);
  lines.push('');

  lines.push(`CURRENT: ${exercise ? exercise.localeName(language) : 'Unknown'}`);
  lines.push(`  Set ${viewSet.index + 1}/${block.sets.length}`);
  if (reps) lines.push(`  Reps: ${reps}`);
  if (weight) lines.push(`  Weight: ${weight}kg`);
  if (duration) lines.push(`  Duration: ${duration}s`);

  const primaryMuscles = exercise?.bodyParts?.map((bp) => bp.names.get(language) || bp.name);
  if (primaryMuscles?.length) {
    lines.push(`  Muscles: ${primaryMuscles.join(', ')}`);
  }

  if (event === 'value_changed') {
    lines.push('');
    lines.push('VALUE CHANGE:');
    if (previousReps !== undefined && reps !== previousReps) {
      lines.push(`  Reps: ${previousReps} -> ${reps}`);
    }
    if (previousWeight !== undefined && weight !== previousWeight) {
      lines.push(`  Weight: ${previousWeight}kg -> ${weight}kg`);
    }
    if (previousDuration !== undefined && duration !== previousDuration) {
      lines.push(`  Duration: ${previousDuration}s -> ${duration}s`);
    }
  }

  if (remainingBreakSeconds !== undefined) {
    lines.push(`BREAK REMAINING: ${remainingBreakSeconds}s`);
  }

  if (isNewExerciseNext && (event === 'set_complete' || event === 'exercise_intro')) {
    lines.push('');
    lines.push(`NEXT: ${nextExercise.localeName(language)}`);
    const nextMuscles = nextExercise.bodyParts?.map((bp) => bp.names.get(language) || bp.name);
    if (nextMuscles?.length) {
      lines.push(`  Targets: ${nextMuscles.join(', ')}`);
    }
  }

  lines.push('');
  if (currentIndex === 0) lines.push('FIRST EXERCISE');
  if (viewSet.index === block.sets.length - 1 && block.sets.length > 1) lines.push('LAST SET of this exercise');
  if (!nextSet) lines.push('FINAL EXERCISE');
  if (block.sets.length === 1) lines.push('Single-set exercise (don\'t say "last set")');

  const breakTime = viewSet.plannedBreakTime || 30;
  const isActiveEvent = event === 'set_starting' || event === 'value_changed';
  const speechTime = isActiveEvent ? 5 : Math.min(15, Math.max(5, breakTime - 5));
  lines.push(`SPEECH TIME: ~${speechTime}s`);
  if (isActiveEvent) {
    lines.push('Active phase - keep it punchy, 1-2 sentences.');
  } else {
    lines.push(`Break time - share useful info! ${speechTime >= 10 ? '2-3 sentences.' : '1-2 sentences.'}`);
  }

  return lines.join('\n');
}

export class WorkoutCoachSession {
  private messages: CoachMessage[] = [];
  private workoutPlan: string = '';
  private language: string;
  private abortController: AbortController | null = null;
  private generationId: number = 0;
  private isGenerating: boolean = false;

  constructor(viewWorkout: ViewWorkout, language: string) {
    this.language = language;
    this.workoutPlan = buildCompactWorkoutPlan(viewWorkout, language);

    this.messages = [
      {
        role: 'system',
        content: buildSystemPrompt(language),
      },
      {
        role: 'user',
        content: `Here's today's workout plan:\n\n${this.workoutPlan}\n\nYou'll receive events as the workout progresses. Respond to each with short, natural spoken text.`,
      },
      {
        role: 'assistant',
        content: "Got it! I'm ready to coach this workout. Let's make it count!",
      },
    ];

    console.info(`${LOG_PREFIX} Session initialized`, {
      language,
      workoutPlanLength: this.workoutPlan.length,
      messageCount: this.messages.length,
    });
  }

  abort(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
      this.isGenerating = false;
      this.generationId++;
      console.info(`${LOG_PREFIX} Aborted in-flight request, new generationId: ${this.generationId}`);
    }
  }

  async generateResponse(
    ctx: CoachEventContext,
    chatFn: (messages: CoachMessage[], options?: Record<string, any>, signal?: AbortSignal) => Promise<string>,
  ): Promise<{ ssml: string; text: string; generationId: number } | null> {
    this.abort();

    this.abortController = new AbortController();
    const myGenerationId = this.generationId;
    this.isGenerating = true;

    const eventContext = buildEventContext(ctx);

    console.info(`${LOG_PREFIX} Generating response`, {
      event: ctx.event,
      generationId: myGenerationId,
      messageCount: this.messages.length,
    });

    try {
      const userMessage: CoachMessage = {
        role: 'user',
        content: eventContext,
      };

      const messagesForApi = [...this.messages, userMessage];

      const startTime = Date.now();
      const response = await chatFn(messagesForApi, {}, this.abortController.signal);
      const duration = Date.now() - startTime;

      if (myGenerationId !== this.generationId) {
        console.warn(`${LOG_PREFIX} Response discarded (stale generationId: ${myGenerationId}, current: ${this.generationId})`);
        return null;
      }

      if (!response) {
        console.warn(`${LOG_PREFIX} Empty response`);
        return null;
      }

      const plainText = response
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim();

      this.messages.push(userMessage);
      this.messages.push({
        role: 'assistant',
        content: response,
      });

      if (this.messages.length > 46) {
        this.messages = [...this.messages.slice(0, 3), ...this.messages.slice(-40)];
        console.info(`${LOG_PREFIX} Trimmed conversation to ${this.messages.length} messages`);
      }

      console.info(`${LOG_PREFIX} Response generated in ${duration}ms`, {
        event: ctx.event,
        ssmlLength: response.length,
        plainText: plainText.substring(0, 100) + (plainText.length > 100 ? '...' : ''),
        messageCount: this.messages.length,
      });

      return {
        ssml: response,
        text: plainText,
        generationId: myGenerationId,
      };
    } catch (error: any) {
      if (error?.name === 'AbortError' || this.abortController?.signal.aborted) {
        console.info(`${LOG_PREFIX} Request aborted`);
        return null;
      }

      console.error(`${LOG_PREFIX} Generation failed`, error?.message || String(error));
      return null;
    } finally {
      this.isGenerating = false;
    }
  }

  getSessionSummary(): { messageCount: number; conversationTokensEstimate: number } {
    const totalChars = this.messages.reduce((sum, m) => sum + m.content.length, 0);
    return {
      messageCount: this.messages.length,
      conversationTokensEstimate: Math.round(totalChars / 4),
    };
  }
}
